import React, { useEffect, useState, useRef, useMemo } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useFormBuilder } from '../utils/useFormBuilder';
import { validateForm } from '../utils/useValidator';
import {
  getMeasurementUnits,
  createMeasurementUnits,
  updateMeasurementUnits,
  deleteMeasurementUnits
} from '../api/measurement-unitsAPI';
import { fetchData } from '../utils/apiHelpers';

const MeasurementUnitLayer = () => {
  const userProfile = useUserProfile();
  const [unitList, setUnitList] = useState([]);
  const [newUnit, setNewUnit] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const inputFields = useMemo(
    () => [
      { field: 'unitName', label: 'Unit Name', type: 'text', size: '6', required: true },
      { field: 'unitCode', label: 'Unit Code', type: 'text', size: '6', required: true },
      { field: 'isUtilisation', label: 'Utilisation Unit', type: 'switch', size: '6' },
    ],
    []
  );

  const { renderField } = useFormBuilder(inputFields, newUnit, setNewUnit, errors);

  const fetchUnits = () => {
    fetchData(getMeasurementUnits, {}, setUnitList);
  };

  const runValidation = () => {
    const newErrors = validateForm(inputFields, newUnit);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (unitList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [unitList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);


  const handleEdit = (item) => {
    setNewUnit({
      ...item,
      isUtilisation: item.isUtilisation ? 1 : 0,
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      await deleteMeasurementUnits(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
    }
  };

  const handleSave = async () => {
    if (!runValidation()) return;
    try {
      const payload = {};
      for (const key in newUnit) {
        if (newUnit[key] !== null && typeof newUnit[key] !== 'object') {
          payload[key] = newUnit[key];
        }
      }

      if (editingId) {
        await updateMeasurementUnits(editingId, payload);
      } else {
        await createMeasurementUnits(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewUnit({ isUtilisation: 0 });
      await fetchUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('measurement-units:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewUnit({ isUtilisation: 0 });
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Unit
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th className="rounded-0">Unit Name</th>
                <th>Unit Code</th>
                <th>Is Utilisation</th>
                <th className="rounded-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {unitList.map((item) => (
                <tr key={item.id}>
                  <td>{item.unitName}</td>
                  <td>{item.unitCode}</td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={item.isUtilisation === 1 || item.isUtilisation === true}
                        disabled
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('measurement-units:update') && (
                        <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleEdit(item)}>
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('measurement-units:delete') && (
                        <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleDelete(item.id)}>
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Unit' : 'New Unit'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {inputFields.map(renderField)}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeasurementUnitLayer;
